export type SiteVariant = 'py' | 'js' | 'desktop';

export interface SiteFeatureFlags {
  authEnabled: boolean; // login/signup flows
  usePyodide: boolean;  // load pyodide in browser
  usePythonSidecar: boolean; // use local python sidecar (Tauri)
  jsRuntime: boolean;   // enable JS execution sandbox
  gcodeGeneration: boolean; // fullcontrol output to gcode
  aiCompletions: boolean; // monacopilot completions
}

export interface SiteConfig {
  id: SiteVariant;
  displayName: string;
  codeLanguageLabel: string; // Shown in editor header
  monacoLanguage: string;    // Monaco language id
  defaultSample: string;     // Default starter code
  feature: SiteFeatureFlags;
  brand: {
    title: string;
    tagline: string;
    ogDescription: string;
  };
}

const PY_SAMPLE = `# Python FullControl sample\nimport fullcontrol as fc\nsteps = []\n# Add your geometry here\n`;
const JS_SAMPLE = `// JavaScript FullControl sample (placeholder)\n// fullcontrol global assumed loaded for js variant\nconst steps = [];\n// Add your geometry logic here\n`;

export const SITE_CONFIG: Record<SiteVariant, SiteConfig> = {
  py: {
    id: 'py',
    displayName: 'py2g',
    codeLanguageLabel: 'Python',
    monacoLanguage: 'python',
    defaultSample: PY_SAMPLE,
    feature: {
      authEnabled: true,
      usePyodide: true,
      usePythonSidecar: false,
      jsRuntime: false,
      gcodeGeneration: true,
      aiCompletions: true,
    },
    brand: {
      title: 'py2g',
      tagline: 'Create G-Code using Python, all in your browser.',
      ogDescription: 'Create G-Code using Python, all in your browser.'
    }
  },
  desktop: {
    id: 'desktop',
    displayName: 'py2g Desktop',
    codeLanguageLabel: 'Python',
    monacoLanguage: 'python',
    defaultSample: PY_SAMPLE,
    feature: {
      authEnabled: true, // We treat it as authenticated
      usePyodide: false, // Desktop doesn't need Pyodide
      usePythonSidecar: true, // Desktop uses local python
      jsRuntime: false,
      gcodeGeneration: true,
      aiCompletions: true,
    },
    brand: {
      title: 'py2g Desktop',
      tagline: 'Create G-Code using Python on your desktop.',
      ogDescription: 'Create G-Code using Python on your desktop.'
    }
  },
  js: {
    id: 'js',
    displayName: 'js2g',
    codeLanguageLabel: 'JavaScript',
    monacoLanguage: 'javascript',
    defaultSample: JS_SAMPLE,
    feature: {
      authEnabled: false, // for now
      usePyodide: false,
      usePythonSidecar: false,
      jsRuntime: true,
      gcodeGeneration: true, // will still generate G-Code via JS lib
      aiCompletions: false, // can enable later
    },
    brand: {
      title: 'js2g',
      tagline: 'Create G-Code using JavaScript, right in your browser.',
      ogDescription: 'Create G-Code using JavaScript, directly in your browser.'
    }
  }
};

export function getSiteConfig(variant: SiteVariant): SiteConfig {
  return SITE_CONFIG[variant] || SITE_CONFIG.py;
}
